import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, debounceTime, delayWhen, distinctUntilChanged, from, map, mergeMap, Observable, of, retryWhen, scan, shareReplay, switchMap, tap, timer, } from 'rxjs';
import { environment } from 'src/environments/environment';
import * as dayjs from 'dayjs';
import * as isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { v4 as uuidv4 } from 'uuid';
import { AdWeek, Event } from '..';
import { OrganizationService } from 'src/app/@shared/services/organization.service';
import { EventDomain } from '../models/event-domain.model';
import { Filter } from 'src/app/@shared/models/filter.model';
import { DEFAULT_PAGING } from 'src/app/@shared/constants/site.constants';

// constants
///TODO: move this to a constants pattern
export const DEFAULT_DATE_RANGE = {
  startDate: '',
  endDate: '',
};

export interface EventStatus {
  Approved: number;
  Pending: number;
  Suppressed: number;
}

@Injectable({
  providedIn: 'root',
})

export class EventService<TEvent extends Event> {
  public selectedEventName: any = null;

  constructor(private httpClient: HttpClient, private organizationService: OrganizationService) { }

  // initialize behavior subjects
  private dateRangeBehaviorSubject = new BehaviorSubject(DEFAULT_DATE_RANGE);
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject({ ...DEFAULT_PAGING, pageSize: 20 });
  private dialogPageBehaviorSubject = new BehaviorSubject({ ...DEFAULT_PAGING, pageSize: 20 });
  private uploadEventPageBehaviorSubject = new BehaviorSubject({ ...DEFAULT_PAGING, pageSize: 20 });
  private uploadEventSortBehaviorSubject = new BehaviorSubject({ active: 'StartDate', direction: 'desc', });
  private cloneEventPageBehaviorSubject = new BehaviorSubject({ ...DEFAULT_PAGING, pageSize: 20 });
  private cloneEventSortBehaviorSubject = new BehaviorSubject({ active: 'StartDate', direction: 'desc', });
  private sortBehaviorSubject = new BehaviorSubject({ active: 'StartDate', direction: 'desc', });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private dialogSearchBehaviorSubject = new BehaviorSubject<string>('');
  private uploadEventSearchBehaviorSubject = new BehaviorSubject<string>('');
  private cloneEventSearchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private viewModeBehaviorSubject = new BehaviorSubject<string>('TABLE');
  private filterBehaviorSubject = new BehaviorSubject<Filter[]>([]);
  private selectedEventForAdBehaviorSubject = new BehaviorSubject<EventDomain<TEvent>[]>([]);
  private showExpiredEventsBehaviorSubject = new BehaviorSubject<boolean>(false);

  // we do not wish to expose our behavior subjects.  create public observables
  public dateRange$ = this.dateRangeBehaviorSubject.asObservable();
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public uploadEventSort$ = this.uploadEventSortBehaviorSubject.asObservable();
  public cloneEventSort$ = this.cloneEventSortBehaviorSubject.asObservable();
  public dialogPage$ = this.dialogPageBehaviorSubject.asObservable();
  public uploadEventPage$ = this.uploadEventPageBehaviorSubject.asObservable();
  public cloneEventPage$ = this.cloneEventPageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public dialogSearch$ = this.dialogSearchBehaviorSubject.asObservable();
  public uploadEventSearch$ = this.uploadEventSearchBehaviorSubject.asObservable();
  public cloneEventSearch$ = this.cloneEventSearchBehaviorSubject.asObservable();
  public viewMode$ = this.viewModeBehaviorSubject.asObservable();
  public filters$ = this.filterBehaviorSubject.asObservable();
  public selectedEventForAd$ = this.selectedEventForAdBehaviorSubject.asObservable();
  public showExpiredEvents$ = this.showExpiredEventsBehaviorSubject.asObservable();

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.pageBehaviorSubject.pipe(debounceTime(300)), // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.filterBehaviorSubject.pipe(debounceTime(50)),
    this.showExpiredEventsBehaviorSubject,
    this.reloadBehaviorSubject
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([page, sort, search, filters, showExpiredEvents, _reload]) => {

      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'DivisionNames' || sort.active == 'EventTypeName') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $expand: 'Detail',
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: _orderby,
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      // if there are filters, add the filters to the parameters
      if (filters.length > 0) {
        params = this.buildFilterParams(filters, params);
      }

      params = params.set('includeExpiredEvents', showExpiredEvents ? 'false' : 'true');

      return params;
    })
  );

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public dialogParams$ = combineLatest([
    this.dialogPageBehaviorSubject.pipe(debounceTime(300)), // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.dialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([page, sort, search, reload]) => {

      let _orderby = `Detail/${sort.active} ${sort.direction}`;
      if (sort.active == 'DivisionNames' || sort.active == 'EventTypeName') {
        _orderby = `${sort.active} ${sort.direction}`;
      }

      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $expand: 'Detail',
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: _orderby,
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  // create the events observable that calls http get when any of our parameters change
  private dialogEventsResponse$ = this.dialogParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/GetEventWithNoAd`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // event listing
  public dialogEvents$: Observable<EventDomain<TEvent>[]> = this.dialogEventsResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of event records based on filtering
  public dialogTotalRecords$: Observable<number> = this.dialogEventsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // set the current page
  dialogPage(page: any) {
    this.dialogPageBehaviorSubject.next(page);
  }

  // sets the search phrase
  dialogSearch(search: string) {
    this.dialogSearchBehaviorSubject.next(search);
    this.dialogPageBehaviorSubject.next({ ...DEFAULT_PAGING, pageSize: 20 });
  }

  clearEventForAd() {
    this.selectedEventForAdBehaviorSubject.next([]);
  }

  setEventForAd(event: EventDomain<TEvent>[]) {
    this.selectedEventForAdBehaviorSubject.next(event);
  }

  public uploadparams$ = combineLatest([
    this.uploadEventPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.uploadEventSortBehaviorSubject,
    this.uploadEventSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([page, sort, search, reload]) => {
      // set the query string parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          $expand: 'Detail',
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `Detail/${sort.active} ${sort.direction}`,
          $count: true,
          includeExpiredEvents: false
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  private statusCache = new Map<string, EventStatus>();
  private pendingRequests = new Map<string, Observable<{ eventId: string; status: EventStatus }>>();

  clearStatusCache() {
    this.statusCache.clear();
  }

  getEventStatuses(eventIds: string[]): Observable<{ [eventId: string]: EventStatus }> {
    return from(eventIds).pipe(
      mergeMap(eventId =>
        this.fetchEventStatus(eventId).pipe(
          map(({ eventId, status }) => ({ [eventId]: status })) // Correct structure
        ),
        3 // Limit concurrent requests
      ),
      scan((acc, statusUpdate) => ({ ...acc, ...statusUpdate }), {} as { [eventId: string]: EventStatus }), // Merge results correctly
      shareReplay(1) // Cache results for multiple subscribers
    );
  }

  private fetchEventStatus(eventId: string): Observable<{ eventId: string; status: EventStatus }> {
    const cachedStatus = this.statusCache.get(eventId);

    // Return cached status if available
    if (cachedStatus) {
      return of({ eventId, status: cachedStatus });
    }

    // If a request is already in progress for this eventId, return that observable to prevent duplicate API calls
    if (!this.pendingRequests.has(eventId)) {
      this.pendingRequests.set(eventId, this.httpClient
        .get<EventStatus>(`${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/GetStatusWiseOfferCopiesByEvent`, {
          params: { eventId },
        })
        .pipe(
          retryWhen(errors =>
            errors.pipe(
              scan((retryCount, err) => {
                if (retryCount >= 3) {
                  throw err; // Stop retrying after 3 attempts
                }
                return retryCount + 1;
              }, 0),
              delayWhen(retryCount => timer(Math.min(1000, 100 * (2 ** retryCount)))) // Cap delay at 1000ms
            )
          ),
          tap(status => {
            this.statusCache.set(eventId, status); // Store in cache
            this.pendingRequests.delete(eventId); // Remove from pending requests
          }),
          map(status => ({ eventId, status })),
          catchError(() => {
            this.pendingRequests.delete(eventId);
            return of({ eventId, status: { Approved: 0, Pending: 0, Suppressed: 0 } }); // Handle error
          }),
          shareReplay(1) // Ensure all subscribers get the same result
        )
      );
    }

    return this.pendingRequests.get(eventId)!;
  }

  // create the events observable that calls http get when any of our parameters change
  private eventsResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // event listing
  public events$: Observable<EventDomain<TEvent>[]> = this.eventsResponse$.pipe(
    map((res: any) => res.value)
  );

  ///TODO: remove this observable
  private alleventsResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/GetAll`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  private uploadEventsResponse$ = this.uploadparams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) =>
      this.httpClient.get(
        `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains`,
        { params: _params })
    ),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );


  // event listing
  public uploadevents$: Observable<EventDomain<TEvent>[]> = this.uploadEventsResponse$.pipe(
    map((res: any) => res.value)
  );

  // event listing
  public allevents$: Observable<EventDomain<TEvent>[]> = this.eventsResponse$.pipe(
    map((res: any) => res.value)
  );

  // event listing
  public eventsList$: Observable<EventDomain<TEvent>[]> = this.alleventsResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of event records based on filtering
  public totalRecords$: Observable<number> = this.eventsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

 
  // total number of event records
  public totalEventsRecords$: Observable<number> = this.alleventsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // total number of event records
  public uploadEventsRecords$: Observable<number> = this.uploadEventsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // event listing by ad week
  public adWeeks$: Observable<AdWeek<EventDomain<TEvent>>[]> = this.eventsResponse$.pipe(
    map((res: any) => this.buildStartDates(res.value))
  );

  // set the current page
  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  uploadEventpage(page: any) {
    this.uploadEventPageBehaviorSubject.next(page);
  }

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
  }

  // sets the sort property and order
  uploadEventsort(sort: any) {
    this.uploadEventSortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  search(search: string) {
    this.searchBehaviorSubject.next(search);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  // sets the search phrase
  uploadEventsearch(search: string) {
    this.uploadEventSearchBehaviorSubject.next(search);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.uploadEventPageBehaviorSubject.next(paging);
  }

  setShowExpiredEvents(show: boolean): void {
    this.showExpiredEventsBehaviorSubject.next(show);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  toggleShowExpiredEvents(): void {
    this.setShowExpiredEvents(!this.showExpiredEventsBehaviorSubject.value);
  }

  /** Align listing query with persisted UI state without resetting pagination (e.g. route init). */
  syncShowExpiredEventsFromUiState(show: boolean): void {
    this.showExpiredEventsBehaviorSubject.next(show);
  }

  /** Restore default listing filter when leaving Event Management (singleton service). */
  resetEventListExpiredVisibility(): void {
    this.showExpiredEventsBehaviorSubject.next(false);
  }

  // reloads/refreshes the event listing
  reload() {
    // reload the event data
    this.reloadBehaviorSubject.next(uuidv4());
  }

  // changes the view mode of the event listing
  toggleViewMode(mode: string) {
    this.viewModeBehaviorSubject.next(mode);
  }

  // sets the date range of the event listing
  dateRange(start?: string, end?: string) {
    const viewMode = this.viewModeBehaviorSubject.value;
    const range = { startDate: start as string, endDate: end as string };
    this.dateRangeBehaviorSubject.next(range);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.pageBehaviorSubject.next(paging);
  }

  // gets an event by id
  getEvent(eventId: string): Observable<TEvent> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/${eventId}`;
    return this.httpClient.get<TEvent>(url).pipe(tap((eventDetails: any) => this.selectedEventName = eventDetails.EventName))
  }

  // an event domain by id
  getEventDomain(eventId: string): Observable<EventDomain<TEvent>> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/${eventId}?$expand=Detail`;
    return this.httpClient.get<EventDomain<TEvent>>(url);
  }

  // up-serts event
  saveEvent(event: TEvent) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/`;
    if (!event.Id || event.Id === '0') {
      // create new record
      return this.httpClient.post(url, event);
    } else {
      // edit existing record
      url += `${event.Id}`;
      return this.httpClient.put(url, event);
    }
  }

  // up-serts event domain
  saveEventDomain(eventDomain: EventDomain<TEvent>) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/`;

    if (!eventDomain.Detail.Id || eventDomain.Detail.Id === '0') {
      // clear the Detail Id
      eventDomain.Detail.Id = '';
      // create new record
      return this.httpClient.post(url, eventDomain);
    } else {
      // edit existing record
      url += `${eventDomain.Detail.Id}`;
      return this.httpClient.put(url, eventDomain);
    }
  }

  updateEventDomain(eventDomain: EventDomain<TEvent>, eventFormData: FormData) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/`;
    if (!eventDomain.Detail.Id || eventDomain.Detail.Id === '0') {
      eventDomain.Detail.Id = '';
      return this.httpClient.post(url, eventFormData, { reportProgress: true, responseType: 'json' });
    } else {
      url += `${eventDomain.Detail.Id}`;
      return this.httpClient.put(url, eventDomain);
    }
  }

  downloadTemplate() {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/DownloadETLTemplate`;
    window.open(url, '_blank');
  }


  // deletes an event by id
  deleteEvent(id: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/${id}`;
    return this.httpClient.delete(url);
  }

  deleteEvents(events: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/DeleteEvents?${this.prepareAssetStringtoDelete(events)}`;
    return this.httpClient.post(url, events);
  }

  prepareAssetStringtoDelete(events: any) {
    let urlParams = '';
    if (events && events.length > 0) {
      for (let index = 0; index <= events.length - 1; index++) {
        urlParams += `eventIds=${events[index]?.Detail?.Id}`;
        if (index != events.length - 1) {
          urlParams += '&'
        }
      }
    }
    return urlParams;
  }

  // adds filters to the event listing
  addFilters(newFilters: Filter[]) {
    const filters = this.filterBehaviorSubject.value;

    newFilters.forEach(filter => {
      if (filters.findIndex(item => item.fieldName.toLowerCase() === filter.fieldName.toLowerCase() && item.value.toLowerCase() === filter.value.toLowerCase()) === -1) {
        filters.push(filter)
      }
    });

    this.filterBehaviorSubject.next(filters);
  }

  // removes a filter from the event listing
  removeFilter(filter: Filter) {
    const filters = this.filterBehaviorSubject.value.filter(item => item !== filter);
    this.filterBehaviorSubject.next(filters)
  }

  // removes a filter from the event listing
  removeFilterByFieldName(fieldName: string) {
    const filters = this.filterBehaviorSubject.value.filter(item => item.fieldName.toLowerCase() !== fieldName.toLowerCase());
    this.filterBehaviorSubject.next(filters)
  }

  // removes all filters for the event listing
  clearFilters() {
    this.filterBehaviorSubject.next([]);
  }

  // builds a list of events by ad week
  private buildAdWeeks(eventDomains: EventDomain<TEvent>[]): AdWeek<EventDomain<TEvent>>[] {

    dayjs.extend(isSameOrAfter);

    const dateRange = this.dateRangeBehaviorSubject.getValue();
    let adWeekArray: AdWeek<EventDomain<TEvent>>[] = [];

    const startDate = dayjs(dateRange.startDate).startOf("week");
    const endDate = dayjs(dateRange.endDate).startOf("week");
    let currentDate = endDate;

    while (currentDate.isSameOrAfter(startDate)) {
      const filteredEvents = eventDomains.filter(eventDomain => {
        return dayjs(eventDomain.Detail.StartDate).isSameOrAfter(currentDate) && dayjs(eventDomain.Detail.StartDate).isBefore(currentDate.add(7, 'day'));
      });

      if (filteredEvents.length > 0) {
        const adWeek: AdWeek<EventDomain<TEvent>> = { title: currentDate.format('MM/DD/YYYY'), eventDomains: filteredEvents };
        adWeekArray.push(adWeek);
      }

      currentDate = currentDate.subtract(7, 'day');
    }

    adWeekArray = adWeekArray.sort((a: any, b: any) => { return Date.parse(a.title) - Date.parse(b.title) });
    return adWeekArray;
  }

  // builds a list of events by start date
  private buildStartDates(eventDomains: EventDomain<TEvent>[]): AdWeek<EventDomain<TEvent>>[] {
    let startDatesArray: AdWeek<EventDomain<TEvent>>[] = [];
    let startDateList = eventDomains
      .map(eDomain => dayjs(eDomain.Detail.StartDate).format('MM/DD/YYYY'))
      .filter((startDate, index, self) => self.indexOf(startDate) === index);

    startDateList.forEach(startDate => {
      let date = dayjs(startDate);
      let filteredEvents = eventDomains.filter(eDomain => date.isSame(dayjs(eDomain.Detail.StartDate), 'day'));

      if (filteredEvents.length > 0) {
        const startDates: AdWeek<EventDomain<TEvent>> = { title: date.format('MM/DD/YYYY'), eventDomains: filteredEvents };
        startDatesArray.push(startDates);
      }
    });

    startDatesArray = startDatesArray.sort((a: any, b: any) => { return Date.parse(a.title) - Date.parse(b.title) });

    return startDatesArray;
  }

  public cloneEventParams$ = combineLatest([
    this.cloneEventPageBehaviorSubject,
    this.cloneEventSortBehaviorSubject,
    this.cloneEventSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
    map(([page, sort, search, _reload]) =>
      this.buildEventDomainsListParams(page, sort, search, [], true)
    )
  );

  private fetchEventDomainsList(params: HttpParams): Observable<any> {
    return of(null).pipe(
      tap(() => this.loadingBehaviorSubject.next(true)),
      switchMap(() =>
        this.httpClient.get(
          `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains`,
          { params })
      ),
      tap(() => this.loadingBehaviorSubject.next(false)),
    );
  }

  private getEventDomainsOrderBy(sort: { active: string; direction: string }): string {
    if (sort.active == 'DivisionNames' || sort.active == 'EventTypeName') {
      return `${sort.active} ${sort.direction}`;
    }
    return `Detail/${sort.active} ${sort.direction}`;
  }

  private buildEventDomainsListParams(
    page: { pageIndex: number; pageSize: number },
    sort: { active: string; direction: string },
    search: string,
    filters: Filter[],
    includeExpiredEvents: boolean
  ): HttpParams {
    let params: HttpParams = new HttpParams({
      fromObject: {
        $expand: 'Detail',
        $skip: page.pageIndex * page.pageSize,
        $top: page.pageSize,
        $orderby: this.getEventDomainsOrderBy(sort),
        $count: true,
      }
    });

    if (search.length) {
      params = params.append('$search', `"${search}"`);
    }

    if (filters.length > 0) {
      params = this.buildFilterParams(filters, params);
    }

    return params.set('includeExpiredEvents', String(includeExpiredEvents));
  }

  cloneEventsearch(search: string) {
    this.cloneEventSearchBehaviorSubject.next(search);
    let paging = JSON.parse(JSON.stringify(DEFAULT_PAGING));
    paging.pageSize = 20;
    this.cloneEventPageBehaviorSubject.next(paging);
  }

  cloneEventsort(sort: any) {
    this.cloneEventSortBehaviorSubject.next(sort);
  }

  cloneEventpage(page: any) {
    this.cloneEventPageBehaviorSubject.next(page);
  }

  private cloneEventsResponse$ = this.cloneEventParams$.pipe(
    switchMap((params) => this.fetchEventDomainsList(params)),
    shareReplay(1)
  );

  public cloneEvents$: Observable<EventDomain<TEvent>[]> = this.cloneEventsResponse$.pipe(
    map((res: any) => res.value)
  );

  public cloneTotalRecords$: Observable<number> = this.cloneEventsResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // build the list of filter parameters
  private buildFilterParams(filters: Filter[], params: HttpParams): HttpParams {
    // get the division id filters
    const divisionIdFilters = filters.filter(item => item.fieldName.toLowerCase() === 'divisionid');

    // loop through the division id filters and add filter statement to param
    divisionIdFilters.forEach((filter) => {
      params = params.append('divisionIds', filter.value);
    });

    // get the event type id filter
    const eventTypeIdFilter = filters.filter(item => item.fieldName.toLowerCase() === 'eventtypeid');

    // loop through the event type id filter and add filter statement to param
    if (eventTypeIdFilter.length > 0) {
      const eventTypeId: any = filters.filter(item => item.fieldName.toLowerCase() === 'eventtypeid')[0].value;
      params = params.append('eventTypeId', eventTypeId);
    }

    const startDateFilter = filters.filter(item => item.fieldName.toLowerCase() === 'startdate');
    if (startDateFilter.length > 0) {
      startDateFilter[0].value = dayjs(startDateFilter[0].value).format('MM/DD/YYYY'),
        params = params.append('windowBegin', startDateFilter[0].value);
    }

    const endDateFilter = filters.filter(item => item.fieldName.toLowerCase() === 'enddate');
    if (endDateFilter.length > 0) {
      endDateFilter[0].value = dayjs(endDateFilter[0].value).format('MM/DD/YYYY'),
        params = params.append('windowEnd', endDateFilter[0].value);
    }

    // return the params
    return params;
  }

  downloadEventsJSON() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/EtlExport`;
    window.open(url, '_blank');
  }

  downloadEventsCSV() {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/Export`;
    window.open(url, '_blank');
  }

  cloneEvent(request: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/CloneEvent`;
    return this.httpClient.post<any>(url, request);
  }

  exportAllGalleyProof(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/ExportGalleyProof?eventId=${eventId}&$orderby=PageNumber asc&mode=cards`;
    return this.httpClient.post(url, {}, { responseType: 'blob', observe: 'response' })
  }

  cloneVersionAndStores(sourceEventId: string, targetEventId: string): Observable<any> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/EventDomains/CloneVersionAndStores`;

    const params = new HttpParams()
      .set('sourceEventId', sourceEventId)
      .set('targetEventId', targetEventId);

    return this.httpClient.post(url, null, { params });
  }

}
